import { createContext, useContext, useState, useEffect, useCallback, useRef, createElement, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ADMIN_CHECK_TIMEOUT_MS = 3000;

/** Race a promise (or thenable) against a hard timeout so a hanging RPC never blocks the UI. */
function withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`[useAuth] ${label} timed out after ${ms}ms`)), ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const adminCacheRef = useRef<Map<string, boolean>>(new Map());

  const checkAdmin = useCallback(async (userId: string): Promise<boolean> => {
    if (adminCacheRef.current.has(userId)) {
      return adminCacheRef.current.get(userId)!;
    }
    try {
      const { data, error } = await withTimeout(
        supabase.rpc("is_current_user_admin"),
        ADMIN_CHECK_TIMEOUT_MS,
        "is_current_user_admin",
      );
      if (error) {
        console.error("[useAuth] is_current_user_admin error:", error);
        return false;
      }
      const result = !!data;
      adminCacheRef.current.set(userId, result);
      return result;
    } catch (e) {
      console.warn("[useAuth] checkAdmin failed/timed out — defaulting to false:", e);
      return false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Subscribe FIRST so we don't miss any auth events.
    // Never `await` inside this callback (deadlock risk) — defer with setTimeout.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!mounted) return;
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          setLoading(true);
          setTimeout(async () => {
            if (!mounted) return;
            const admin = await checkAdmin(newSession.user.id);
            if (!mounted) return;
            setIsAdmin(admin);
            setLoading(false);
          }, 0);
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // THEN restore the existing session — also bounded by a hard timeout.
    (async () => {
      try {
        const { data: { session: existingSession } } = await withTimeout(
          supabase.auth.getSession(),
          ADMIN_CHECK_TIMEOUT_MS,
          "getSession",
        );
        if (!mounted) return;
        setSession(existingSession);
        setUser(existingSession?.user ?? null);
        if (existingSession?.user) {
          const admin = await checkAdmin(existingSession.user.id);
          if (!mounted) return;
          setIsAdmin(admin);
        }
      } catch (e) {
        console.warn("[useAuth] getSession failed/timed out — proceeding unauthenticated:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [checkAdmin]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    adminCacheRef.current.clear();
    await supabase.auth.signOut();
  }, []);

  const value: AuthContextValue = { user, session, loading, isAdmin, signIn, signOut };
  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
